import React from 'react';
import { getSocket } from '@/lib/socket';
import { usePlayerStore } from '@/stores/playerStore';
import { useRoomStore } from '@/stores/roomStore';
import { Button } from '@/components/Button';

interface ControlsProps {
  roomId: string;
}

export const Controls: React.FC<ControlsProps> = () => {
  const socket = getSocket();
  const { isPlaying, videoId } = usePlayerStore();
  const { myRole, currentIndex, playlist } = useRoomStore();

  const canControl = myRole === 'owner' || myRole === 'dj';

  const handlePlayPause = () => {
    if (!canControl) return;

    // If no video is loaded but we have tracks in the playlist, start playing
    if (!videoId && playlist.length > 0) {
      socket.emit('sync:play');
      return;
    }

    if (!videoId) return;

    if (isPlaying) {
      socket.emit('sync:pause');
    } else {
      socket.emit('sync:resume');
    }
  };

  const handlePrevious = () => {
    if (!canControl) return;
    socket.emit('playlist:prev');
  };

  const handleNext = () => {
    if (!canControl) return;
    socket.emit('playlist:next');
  };

  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < playlist.length - 1;

  return (
    <div className="flex items-center justify-center gap-4 py-4">
      <Button
        variant="ghost"
        size="lg"
        onClick={handlePrevious}
        disabled={!canControl || !hasPrevious}
        className="rounded-full p-3"
        title="Previous track"
      >
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path d="M8.445 14.832A1 1 0 0010 14v-2.798l5.445 3.63A1 1 0 0017 14V6a1 1 0 00-1.555-.832L10 8.798V6a1 1 0 00-1.555-.832l-6 4a1 1 0 000 1.664l6 4z" />
        </svg>
      </Button>

      <Button
        variant="primary"
        size="lg"
        onClick={handlePlayPause}
        disabled={!canControl || (playlist.length === 0)}
        className="rounded-full p-4 shadow-lg"
        title={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? (
          <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
          </svg>
        )}
      </Button>

      <Button
        variant="ghost"
        size="lg"
        onClick={handleNext}
        disabled={!canControl || !hasNext}
        className="rounded-full p-3"
        title="Next track"
      >
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path d="M4.555 5.168A1 1 0 003 6v8a1 1 0 001.555.832L10 11.202V14a1 1 0 001.555.832l6-4a1 1 0 000-1.664l-6-4A1 1 0 0010 6v2.798l-5.445-3.63z" />
        </svg>
      </Button>

      {!canControl && (
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2">
          <span className="text-xs text-tertiary bg-muted px-3 py-1 rounded-full">
            Only DJ and Owner can control playback
          </span>
        </div>
      )}
    </div>
  );
};
